/**
 * Word Selector Component
 * Product Selector Quote Form
 * @author Rhys Lloyd
 */

//
// Utilities
import debug from "debug";
const MODULE_NAME = 'word-selector';
const log = debug(MODULE_NAME);


//
// Dependencies
const Vue = window.Vue;
import memoize from 'lodash/memoize';
import { wordSelectorTemplate } from '../templates';
import { MODULE_WORD_SELECTOR } from '../config';
import uniqueId from 'lodash/uniqueId';

/**
 * Word Selector Vue Component
 * @description Designed to implement the template: `product-selector-quote-form-word-selector.html`
 */
export const WordSelectorComponent = memoize((context) => {
	log('Loading component with context', context);
	return Vue.extend({
		template: wordSelectorTemplate(context),
		props: {
			id: {
				type: String,
				default () {
					return uniqueId(MODULE_WORD_SELECTOR);
				},
			},
			name: String,
			items: {
				type: Array,
			},
			selected: {
				type: String,
			},
			defaultSelected: {
				type: String,
			},
			isMatrixWord: Boolean,
			dropdownModifier: String,
		},

		data: () => ({
			dropdownOpen: false,
			focusedItem: '',
			options: {},
		}),

		compiled () {
			// Parse Items
			this.items.forEach((item, i) => {
				let $el = this.$el.querySelector(`[data-value="${item.value}"]`),
					id = uniqueId(`${this.id}-${item.value.toString().toUpperCase()}`);

				// Assign ID to $el
				$el.id = id;

				this.options[item.value] = {
					index: i,
					label: item.label,
					icon: item.icon,
					$el: $el,
					id: id,
				};

				if (item.selected && !this.defaultSelected) {
					this.defaultSelected = item.value;
				}
			});

			if (!this.selected) {
				// If nothing selected, select the default or the default
				this.selectItem(this.defaultSelected ? this.defaultSelected : this.items[0].value, false, false);
			}

			this.focusedItem = this.selected;
		},

		computed: {
			selectedLabel () {
				return this.selected && this.options[this.selected] ? this.options[this.selected].label : null;
			},

			selectedOptionElementId () {
				return this.selected && this.options[this.selected] ? this.options[this.selected].id : null;
			},
		},

		methods: {
			/**
			 * Select a dropdown item.
			 * @param {String} item - Key of the item to select
			 * @param {Boolean} [toggleDropdown=false] - Optionally toggle the dropdown
			 * @param {Boolean} [dispatchEvent=true] - Optionally dispatch selection event
			 */
			selectItem (item, toggleDropdown = false, dispatchEvent = true) {
				log('selectItem(item):', item);
				this.selected = item;
				this.focusedItem = item;
				this.focusItem(item);

				if (toggleDropdown) {
					this.toggleDropdown();
				}

				if (this.isMatrixWord && dispatchEvent) {
					this.$dispatch('matrix-word-selected', this.selected);
				}
			},

			/**
			 * Focus on oneself.
			 * @param {Event} [event]
			 * @param {Boolean} [toggleDropdown=false] - Optionally toggle the dropdown
			 */
			focusSelf (event, toggleDropdown = false) {
				log('focusSelf(event, toggleDropdown)', event, toggleDropdown);

				if (toggleDropdown) {
					this.toggleDropdown();
				}

				setTimeout(() => {
					this.$el.focus();
				}, 0);
			},

			/**
			 * Focus an item in the dropdown.
			 * @param {String} item - Key of the item to focus
			 */
			focusItem (item) {
				// Timeout required for focus to happen (redirect focus after browser focuses the element clicked)
				setTimeout(() => {
					log('focusItem', this.options[item].$el);
					this.options[item].$el.focus();
					this.focusedItem = item;
				}, 0);
			},

			/**
			 * Select the next item in the dropdown.
			 */
			selectNext () {
				let currentOptionIndex = this.options[this.selected].index,
					nextIndex = currentOptionIndex < this.items.length - 1 ?
					currentOptionIndex + 1 :
					this.items.length - 1,
					nextOptionKey = this.items[nextIndex].value;

				log('selectNext', nextOptionKey);
				this.selectItem(nextOptionKey);
			},

			/**
			 * Select the previous item in the dropdown.
			 */
			selectPrevious () {
				let currentOptionIndex = this.options[this.selected].index,
					previousIndex = currentOptionIndex ? currentOptionIndex - 1 : 0,
					previousOptionKey = this.items[previousIndex].value;

				log('selectPrevious', previousOptionKey);
				this.selectItem(previousOptionKey);
			},

			/**
			 * Toggles the dropdown open or closed, based on its current state.
			 * @param {Event} [event]
			 * @param {Boolean} [forceClose=false] - forces the dropdown to close
			 */
			toggleDropdown (event, forceClose = false) {
				log(`toggleDropdown() for "${this.name}", forceClose:`, forceClose);
				this.dropdownOpen = forceClose ? false : !this.dropdownOpen;

				if (this.dropdownOpen) {
					// Toggle Open
					this.focusItem(this.selected);
				} else {
					// Toggle Closed
					this.focusSelf();
				}
			},
		},
	});
});

export {WordSelectorComponent as default};



// WEBPACK FOOTER //
// ./project/client/src/core/modules/ps-quote-form/components/word-selector.js